// ════════════════════════════════════════════════════════════════════════════
//  ManDana — a line begins with a capital
//  ---------------------------------------------------------------------------
//  Every writing surface a person has used for the last fifteen years — the
//  keyboard on their phone, Notes, Mail, Word — puts a capital on the first
//  letter of a line without being asked. The record's editor is a
//  contentEditable on a page, so it inherits none of that: on a laptop nothing
//  capitalises anything, and the writer's own notes come out looking like a
//  chat log. `autocapitalize="sentences"` is not the answer either — it is a
//  hint to a SOFTWARE keyboard, ignored on desktop and unavailable to a
//  contentEditable this file re-renders on every keystroke.
//
//  So the rule is ours, and it is deliberately narrow: the ONE letter that
//  opens a line is raised, and nothing else. Concretely it fires only when all
//  of these hold at the moment of the keystroke —
//    • exactly one character was inserted, and the caret sits just after it
//      (typing, dictation, a software keyboard — never a paste, never undo);
//    • it is a lowercase letter with a single-character uppercase form (so 'ß'
//      is left alone rather than becoming "SS", and Arabic, which has no case,
//      is untouched by construction);
//    • everything to its left on the line is the line's OPENING — indent, then
//      an optional block marker, then any emphasis or link syntax that has been
//      opened and not yet closed. Both halves are read through md-unformat.ts's
//      grammar, so a bullet, a number, a to-do, a heading and a quote all count
//      as "the start", and so does the `**` of a line that begins bold. A
//      marker is not text: `**milestone` has its first LETTER at the third
//      character, and that is the one the writer sees first;
//    • nothing sits to its right: the caret is at the end of the line. Typing
//      in front of text that is already there is joining a sentence, not
//      starting one;
//    • the line is not inside a fenced code block, and does not open with an
//      inline `` ` `` — code is the one run where case is meaning, so it is left
//      exactly as typed, on the same grounds as the fence.
//
//  ONE MORE THING IS TAKEN BACK, AFTER THE FACT. A line of its own that holds
//  nothing but an address is the editor's embed gesture, and `Https://…` is not
//  how anybody writes a URL. There is no way to know at the `h` that a URL is
//  coming — so the capital is raised, and lowered again the moment the line
//  turns out to be one. That is only possible while we still know the capital
//  was ours, which is what the memo below is already for. md-link.ts decides
//  what counts as an address; nothing here re-guesses it.
//
//  THE WRITER MUST BE ABLE TO SAY NO, or the rule is a cage. Backspace over a
//  capital this made and type the lowercase letter again, and it stays lower —
//  the same escape hatch iOS gives, and the reason this function takes and
//  returns a memo.
//
//  The memo has to survive the way people actually erase, which is not one
//  keystroke. Nobody watches the capital appear, presses Backspace once and
//  retypes; they finish the word, dislike it, and hold Backspace back through
//  it. So the memo lives for as long as THE CAPITAL ITSELF IS STILL STANDING
//  and every edit since has happened to its right — which is exactly the span
//  in which the writer can still be walking back to it. Touch anything to its
//  left, or replace it, and the memo is gone; there is nothing left to refuse.
//
//  Pure string work — a source, a caret and the source and caret they become —
//  for the reason md-list.ts, md-link.ts and md-unformat.ts give: this rewrites
//  the writer's text underneath their caret, and a contentEditable is the one
//  place in this app that cannot be unit-tested. Pinned in
//  tests/capitalize.test.js.
// ════════════════════════════════════════════════════════════════════════════

/** Where the last auto-capital happened, and where one was refused.
    `made` is the offset of a capital this rule raised and `char` is the letter
    it put there — together they are the only place a refusal can be recognised,
    and `char` is what says the capital is still the one we made rather than
    something that has since taken its place. `declined` is that offset once the
    writer has deleted it, which suppresses the rule for the retype that
    follows. */
interface MdCapsMemo {
  made: number | null;
  char: string | null;
  declined: number | null;
}

/** A source and caret after the rule has had its say, plus the memo to keep.
    The source and caret come back unchanged when the rule does not fire — the
    memo is still the caller's to store, because a keystroke that changes
    nothing is exactly what ends a refusal. */
interface MdCapsStep {
  src: string;
  caret: number;
  memo: MdCapsMemo;
}

(function (g: any) {
  'use strict';

  const MD_CAPS_NONE: MdCapsMemo = { made: null, char: null, declined: null };

  /** A memo from a caller, normalised. Anything unrecognisable is "no memo",
      which only ever costs one unwanted capital. */
  function mdCapsMemo(memo: unknown): MdCapsMemo {
    const m = memo as MdCapsMemo | null;
    if (!m || typeof m !== 'object') return MD_CAPS_NONE;
    const char = typeof m.char === 'string' && m.char.length === 1 ? m.char : null;
    const made = char != null && typeof m.made === 'number' ? m.made : null;
    const declined = typeof m.declined === 'number' ? m.declined : null;
    return { made, char: made == null ? null : char, declined };
  }

  /** The single character just inserted at `caret`, or null for anything else —
      a paste, a multi-character run, a deletion, a replacement, a no-op. */
  function mdCapsInserted(prev: string, src: string, caret: number): string | null {
    const at = caret - 1;
    if (at < 0 || src.length !== prev.length + 1) return null;
    if (src.slice(0, at) !== prev.slice(0, at)) return null;
    if (src.slice(caret) !== prev.slice(at)) return null;
    return src.charAt(at);
  }

  /** True when this edit deleted exactly the one character that sat at `at` —
      the Backspace that finishes a refusal. */
  function mdCapsDeleted(prev: string, src: string, caret: number, at: number): boolean {
    if (at < 0 || prev.length !== src.length + 1 || caret !== at) return false;
    return src.slice(0, at) === prev.slice(0, at) && src.slice(at) === prev.slice(at + 1);
  }

  /* An address the writer is part-way through typing, whose first letter this
     rule raised before there was any way to know it was an address. Returns the
     source with that letter put back, or null.

     Only ever asked while the capital is still ours to take back, so a capital
     the WRITER typed into a URL is left exactly where they put it. md-link.ts
     is what decides an address is an address — the same scan that lights the
     link up and that md-embed.ts reads to turn the line into a card. */
  function mdCapsUnURL(state: MdCapsMemo, src: string): string | null {
    const at = state.made;
    if (at == null || state.char == null || typeof g.mdLinkScan !== 'function') return null;
    let le = src.indexOf('\n', at);
    if (le < 0) le = src.length;
    const found = g.mdLinkScan(src.slice(at, le)) as { start: number }[] | null;
    if (!found || !found.length || found[0]?.start !== 0) return null;
    return src.slice(0, at) + state.char.toLowerCase() + src.slice(at + 1);
  }

  /* What is left of the memo after this edit, before the rule has its say.

     A capital keeps its standing while it is still THERE and untouched — every
     character of `src` up to it unchanged, and the capital itself still the one
     we put there. That covers the whole of an ordinary erase: finishing a word,
     changing your mind, and holding Backspace back through it. The moment the
     capital itself goes, the refusal is armed. */
  function mdCapsCarry(state: MdCapsMemo, prev: string, src: string, caret: number): MdCapsMemo {
    const at = state.made;
    if (at == null || state.char == null) return MD_CAPS_NONE;
    if (mdCapsDeleted(prev, src, caret, at)) return { made: null, char: null, declined: at };
    if (src.charAt(at) !== state.char) return MD_CAPS_NONE;
    if (src.slice(0, at) !== prev.slice(0, at)) return MD_CAPS_NONE;
    return { made: at, char: state.char, declined: null };
  }

  /* The emphasis and link syntax a line can OPEN with, mirroring the runs
     md-unformat.ts's MD_INLINE_RE knows and md-markdown.jsx draws: bold before
     italic (the looser pattern would otherwise eat half of `**`), and a link's
     opening bracket.

     Two are deliberately absent. An inline `` ` `` is code, and case is meaning
     there for exactly the reason a fenced block is left alone. And `[[` opens a
     WIKILINK, which is a record's name being looked up rather than prose being
     written — raising its first letter would fight the very list of records the
     writer is choosing from. */
  const MD_OPENERS_RE = /^(?:\*\*|\*|\[(?!\[))*/;

  /** Is the offset inside a fenced code block? Counted from the top, because a
      fence is opened and closed by whole lines and nothing else. */
  function mdCapsFenced(lines: string[], line: number): boolean {
    let open = false;
    for (let i = 0; i < line; i++) {
      if (/^\s*(```|~~~)/.test(lines[i] as string)) open = !open;
    }
    return open;
  }

  /* The rule itself.

     Inputs
       prev           the source as it was before this edit
       src            the source as it is now
       caret          the collapsed caret in `src`, as a character offset
       memo           what the previous call returned (null on the first)
       firstLineTitle is line 0 the record's NAME? (title mode — see mdLineKind)

     Returns the source and caret to use, and the memo to keep. It ALWAYS
     returns: "the rule did not fire" is a step whose src and caret are the ones
     that went in, and the memo has to be carried either way. */
  function mdAutoCapitalize(prev: unknown, src: unknown, caret: unknown,
    memo?: unknown, firstLineTitle?: boolean): MdCapsStep {
    const before = typeof prev === 'string' ? prev : '';
    const text = typeof src === 'string' ? src : '';
    const off = typeof caret === 'number' && isFinite(caret)
      ? Math.max(0, Math.min(caret, text.length)) : -1;
    const state = mdCapsMemo(memo);
    const nothing: MdCapsStep = { src: text, caret: off < 0 ? 0 : off, memo: MD_CAPS_NONE };
    if (typeof src !== 'string' || off < 0) return nothing;

    /* What the previous capital still amounts to. This is worked out for EVERY
       edit, insertion or not — a memo that only survived keystrokes of one kind
       would not survive the erase it exists for. */
    const carried = mdCapsCarry(state, before, text, off);
    /* Before anything else: if the capital we are still holding turns out to
       have been the first letter of an address, give it back. */
    const address = mdCapsUnURL(carried, text);
    if (address != null) return { src: address, caret: off, memo: MD_CAPS_NONE };
    const kept: MdCapsStep = { src: text, caret: off, memo: carried };

    const ch = mdCapsInserted(before, text, off);
    if (ch == null) return kept;

    const at = off - 1;
    if (state.declined === at) return nothing;      // asked once, answered once

    const upper = ch.toUpperCase();
    if (upper === ch || upper.length !== 1 || ch.toLowerCase() !== ch) return kept;

    /* The caret must be at the end of its line: this is a line being STARTED,
       not a sentence being pushed in front of one that is already written. */
    const ls = text.lastIndexOf('\n', at - 1) + 1;
    let le = text.indexOf('\n', at);
    if (le < 0) le = text.length;
    if (off !== le) return kept;

    const lines = text.split('\n');
    const index = text.slice(0, ls).split('\n').length - 1;
    if (mdCapsFenced(lines, index)) return kept;

    /* Everything to the left must be the line's opening. In title mode line 0
       wears no marker — the same exception mdUnformat makes, for the same
       reason: the record's name is not a block. */
    const titleLine = !!firstLineTitle && index === 0;
    const opening = text.slice(ls, at);
    const prefix = !titleLine && g.mdBlockPrefix
      ? g.mdBlockPrefix(opening)
      : { pad: /^[ \t]*/.exec(opening)?.[0] || '', marker: '' };
    const block = (prefix.pad as string).length + (prefix.marker as string).length;
    /* …and then whatever emphasis the line has opened and not closed. Syntax is
       not text: the first letter of `- **milestone` is the m, not the star. */
    const inline = MD_OPENERS_RE.exec(opening.slice(block))?.[0].length || 0;
    if (ls + block + inline !== at) return kept;

    return {
      src: text.slice(0, at) + upper + text.slice(off),
      caret: off,
      memo: { made: at, char: upper, declined: null }
    };
  }

  g.mdAutoCapitalize = mdAutoCapitalize;

  // CommonJS escape hatch, following md-list.ts and md-unformat.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { mdAutoCapitalize };
  }
})(typeof window !== 'undefined' ? window : globalThis);
